import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';
import { captureError } from '@/lib/sentry';
import type { ContactDetails } from '@/types/duplicate';

interface DeleteContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactDetails | null;
  onSuccess: () => void;
}

export function DeleteContactDialog({
  open,
  onOpenChange,
  contact,
  onSuccess,
}: DeleteContactDialogProps) {
  const [deleteText, setDeleteText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) {
      setDeleteText('');
      setProgress(0);
    }
  }, [open]);

  useEffect(() => {
    let interval: number | null = null;
    
    if (isDeleting) {
      interval = window.setInterval(() => {
        setProgress(prev => {
          // Progress goes from 0 to 100 over 15 seconds
          const newProgress = prev + (100 / (15 * (1000 / 100)));
          return newProgress > 100 ? 100 : newProgress;
        });
      }, 100);
    }
    
    return () => {
      if (interval !== null) {
        clearInterval(interval);
      }
    };
  }, [isDeleting]);

  const handleDelete = async () => {
    if (!contact || deleteText !== 'delete') return;
    
    setIsDeleting(true);

    // Create a toast notification that will show progress
    toast({
      title: "Deleting contact...",
      description: (
        <div className="w-full space-y-2">
          <Progress value={progress} className="h-2 w-full" />
          <p className="text-xs">This may take a few moments</p>
        </div>
      ),
      duration: 15000,
    });

    try {
      // Delete the contact using delete_contacts_safely RPC
      const { error: rpcError } = await supabase.rpc('delete_contacts_safely', {
        contact_ids: [contact.id]
      });

      if (rpcError) {
        console.warn("RPC delete failed, trying soft delete fallback:", rpcError);
        // Fallback to soft delete
        const { error } = await supabase
          .from("contacts")
          .update({ deleted_at: new Date().toISOString() })
          .eq("id", contact.id)
          .is("deleted_at", null);

        if (error) throw error;
      }

      // Update toast notification
      toast({
        title: "Contact deleted",
        description: "The contact has been successfully deleted.",
      });
      
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error('Error deleting contact:', error);
      captureError(error as Error, { feature: 'duplicates-delete-contact' });
      toast({
        title: "Error",
        description: error?.message || "Failed to delete contact. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setProgress(100);
    }
  };

  if (!contact) return null;

  const contactName = contact.is_company 
    ? contact.company_name 
    : `${contact.first_name} ${contact.last_name}`;

  return (
    <Dialog open={open} onOpenChange={isDeleting ? undefined : onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Contact</DialogTitle>
          <DialogDescription>
            Please confirm that you want to delete this contact.
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Contact information */}
          <div className="space-y-2">
            <Label>Contact Information</Label>
            <div className="border rounded-lg p-4 space-y-2">
              <div className="font-medium text-lg">{contactName}</div>
              
              {contact.job_title && (
                <div className="text-sm">{contact.job_title}</div>
              )}
              
              {!contact.is_company && contact.company_name && (
                <div className="text-sm">{contact.company_name}</div>
              )}
              
              <div className="flex flex-wrap gap-2 mt-2">
                {contact.type && (
                  <Badge variant="outline">{contact.type}</Badge>
                )}
                {contact.rank && (
                  <Badge variant="outline">{contact.rank}</Badge>
                )}
                {contact.status && (
                  <Badge variant="outline">{contact.status}</Badge>
                )}
                {contact.relationship && (
                  <Badge variant="outline">{contact.relationship}</Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="border-t pt-4">
            <Label htmlFor="confirm">Type 'delete' to confirm</Label>
            <Input
              id="confirm"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder="Type 'delete' to confirm"
              className="mt-2"
              disabled={isDeleting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteText !== 'delete' || isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Confirm Delete'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}